/**
 * Template Service for Prompt Profiles
 * Handles template loading, version management, and template operations
 */

export interface PromptTemplate {
  name: string;
  prompt_type: 'image' | 'video';
  asset_type: string;
  description: string;
  // version: auto-managed by backend
  system_role_definition: string;
  custom_system_instructions: string;
  custom_user_instructions: string;
  custom_output_format: string;
  custom_metadata_schema: Record<string, any>;
  custom_system_focus_areas: Record<string, string[]>;
  custom_user_focus_areas: Record<string, string[]>;
  llm_parameters: Record<string, any>;
  validation_rules: Record<string, any>;
}

export class TemplateService {
  private static templates: Record<string, PromptTemplate> = {
    'Basic Image Analysis': {
      name: "Basic Image Analysis",
      prompt_type: "image",
      asset_type: "image",
      description: "Basic image analysis prompt for general use",
      // version: auto-managed by backend
      system_role_definition: "You are an expert image content analyzer specializing in visual assessment and detailed analysis.",
      custom_system_instructions: "Analyze the provided image comprehensively, focusing on visual elements, composition, and technical quality.",
      custom_user_instructions: "Please provide a detailed analysis of this image including composition, color theory, lighting, and overall visual impact.",
      custom_output_format: "Provide analysis in JSON format with quality_score, tags, description, and technical_notes fields.",
      custom_metadata_schema: {
        quality_score: { type: "number", required: true, min: 0, max: 10 },
        tags: { type: "array", required: true },
        description: { type: "string", required: true, min_length: 50 },
        technical_notes: { type: "string", required: false }
      },
      custom_system_focus_areas: {
        image: ["Composition", "Color Theory", "Lighting", "Technical Quality", "Visual Impact"]
      },
      custom_user_focus_areas: {
        image: ["Visual Elements", "Artistic Style", "Subject Clarity", "Overall Aesthetics"]
      },
      llm_parameters: {
        temperature: 0.7,
        max_tokens: 1000,
        top_p: 1.0,
        frequency_penalty: 0.0,
        presence_penalty: 0.0
      },
      validation_rules: {
        required_fields: ["quality_score", "tags", "description"],
        min_description_length: 50,
        max_tags: 10,
        quality_score_range: { min: 0, max: 10 }
      }
    },

    'Advanced Video Analysis': {
      name: "Advanced Video Analysis",
      prompt_type: "video",
      asset_type: "video",
      description: "Advanced video analysis with motion and temporal considerations",
      // version: auto-managed by backend
      system_role_definition: "You are an expert video content analyzer specializing in motion analysis, temporal dynamics, and cinematic quality assessment.",
      custom_system_instructions: "Analyze the provided video comprehensively, considering motion quality, temporal dynamics, visual effects, and overall cinematic impact.",
      custom_user_instructions: "Please provide a detailed analysis of this video including motion quality, frame rate analysis, visual effects, and temporal dynamics.",
      custom_output_format: "Provide analysis in JSON format with motion_score, temporal_analysis, visual_effects, and cinematic_quality fields.",
      custom_metadata_schema: {
        motion_score: { type: "number", required: true, min: 0, max: 10 },
        temporal_analysis: { type: "object", required: true },
        visual_effects: { type: "array", required: false },
        cinematic_quality: { type: "number", required: true, min: 0, max: 10 },
        description: { type: "string", required: true, min_length: 100 }
      },
      custom_system_focus_areas: {
        video: ["Motion Quality", "Temporal Dynamics", "Visual Effects", "Cinematic Quality", "Audio-Visual Sync"]
      },
      custom_user_focus_areas: {
        video: ["Frame Rate Analysis", "Scene Transitions", "Visual Impact", "Technical Excellence"]
      },
      llm_parameters: {
        temperature: 0.8,
        max_tokens: 1500,
        top_p: 1.0,
        frequency_penalty: 0.1,
        presence_penalty: 0.1
      },
      validation_rules: {
        required_fields: ["motion_score", "temporal_analysis", "cinematic_quality", "description"],
        min_description_length: 100,
        quality_score_range: { min: 0, max: 10 }
      }
    },

    '3D Model Analysis': {
      name: "3D Model Analysis",
      prompt_type: "image",
      asset_type: "3d_model",
      description: "Specialized analysis for 3D models and assets",
      // version: auto-managed by backend
      system_role_definition: "You are an expert 3D model analyst specializing in geometry, topology, materials, and technical quality assessment.",
      custom_system_instructions: "Analyze the provided 3D model comprehensively, focusing on geometry quality, topology, materials, and technical specifications.",
      custom_user_instructions: "Please provide a detailed analysis of this 3D model including geometry quality, topology analysis, material assessment, and technical specifications.",
      custom_output_format: "Provide analysis in JSON format with geometry_score, topology_quality, material_assessment, and technical_specs fields.",
      custom_metadata_schema: {
        geometry_score: { type: "number", required: true, min: 0, max: 10 },
        topology_quality: { type: "string", required: true },
        material_assessment: { type: "object", required: true },
        technical_specs: { type: "object", required: true },
        description: { type: "string", required: true, min_length: 80 }
      },
      custom_system_focus_areas: {
        "3d_model": ["Geometry Quality", "Topology Analysis", "Material Assessment", "Technical Specifications", "Optimization"]
      },
      custom_user_focus_areas: {
        "3d_model": ["Model Complexity", "UV Mapping", "Polygon Count", "Rendering Quality", "Performance"]
      },
      llm_parameters: {
        temperature: 0.6,
        max_tokens: 1200,
        top_p: 1.0,
        frequency_penalty: 0.0,
        presence_penalty: 0.0
      },
      validation_rules: {
        required_fields: ["geometry_score", "topology_quality", "material_assessment", "technical_specs", "description"],
        min_description_length: 80,
        quality_score_range: { min: 0, max: 10 }
      }
    }
  };

  /**
   * Get all available template names
   */
  static getTemplateNames(): string[] {
    return Object.keys(this.templates);
  }

  /**
   * Get a specific template by name
   */
  static getTemplate(name: string): PromptTemplate | null {
    return this.templates[name] || null;
  }

  /**
   * Get template as JSON string for editor
   */
  static getTemplateAsJson(name: string): string {
    const template = this.getTemplate(name);
    if (!template) {
      throw new Error(`Template '${name}' not found`);
    }
    return JSON.stringify(template, null, 2);
  }

  /**
   * Get template with dynamic version (for new profiles)
   * Version will be generated by backend
   */
  static getTemplateForNewProfile(name: string): PromptTemplate {
    const template = this.getTemplate(name);
    if (!template) {
      throw new Error(`Template '${name}' not found`);
    }
    
    return {
      ...template
      // version: auto-managed by backend
    };
  }

  /**
   * Validate template structure
   */
  static validateTemplate(template: any): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!template.name) errors.push("Name is required");
    if (!template.prompt_type) errors.push("Prompt type is required");
    if (!template.asset_type) errors.push("Asset type is required");
    if (!template.description) errors.push("Description is required");
    
    if (!template.custom_system_instructions && !template.custom_user_instructions) {
      errors.push("At least one instruction field is required");
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Create a new template (for future extension)
   */
  static createTemplate(template: PromptTemplate): void {
    this.templates[template.name] = template;
  }

  /**
   * Update an existing template (for future extension)
   */
  static updateTemplate(name: string, template: PromptTemplate): void {
    if (!this.templates[name]) {
      throw new Error(`Template '${name}' not found`);
    }
    this.templates[name] = template;
  }

  /**
   * Delete a template (for future extension)
   */
  static deleteTemplate(name: string): void {
    if (!this.templates[name]) {
      throw new Error(`Template '${name}' not found`);
    }
    delete this.templates[name];
  }
}

export default TemplateService;

